import React, { useState, useEffect } from 'react';
import type { FC } from 'react';

import { BrokenThumbnailIcon } from '@/common/broken-thumbnail-icon';
import { Menu, type MenuItem } from '@/common/menu';
import { timeAgo } from '@/utils';

interface DraftTemplateTileProps {
    title: string;
    createdAt: string;
    thumbnail: string;
    menuItems?: MenuItem[];
    isRenaming?: boolean;
    onRename?: (title: string) => void;
    onClick?: () => void;
}

export const DraftTemplateTile: FC<DraftTemplateTileProps> = ({
    title,
    createdAt,
    thumbnail,
    menuItems,
    isRenaming,
    onRename,
    onClick,
}) => {
    const [titleValue, setTitleValue] = useState(title);
    const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState(false);

    useEffect(() => {
        if (!isRenaming) {
            setTitleValue(title);
        }
    }, [isRenaming, title]);

    return (
        <div
            className="flex flex-row min-w-[300px] max-w-[480px] w-full border-gray-130 border-[1px] border-solid rounded-md relative"
            onClick={onClick}
            onKeyUp={isRenaming ? () => {} : (e) => e.key === 'Enter' && onClick && onClick()}
        >
            <div className="flex items-center justify-center w-[28%] aspect-square bg-black-50 rounded chess-sm">
                {thumbnailLoadFailed || !thumbnail ? (
                    <BrokenThumbnailIcon className="w-2/3 h-2/3" />
                ) : (
                    <img
                        src={thumbnail}
                        alt="thumbnail"
                        className="w-full aspect-square object-contain"
                        onError={(e) => {
                            setThumbnailLoadFailed(true);
                        }}
                    />
                )}
            </div>
            <div className="flex flex-col pl-4 pr-2 py-3 w-[72%]">
                <div className="flex flex-row justify-between items-center w-full h-9">
                    <div className="text-red-50 text-sm bg-red-05 bg-opacity-20 px-3 py-1 rounded ">Draft</div>
                    {menuItems && <Menu items={menuItems} />}
                </div>
                {isRenaming ? (
                    <input
                        type="text"
                        className="mr-4 text-white bg-transparent border-none outline-none text-base truncate w-full overflow-hidden whitespace-nowrap"
                        onKeyUp={(e) => e.key === 'Enter' && onRename?.(titleValue)}
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        // biome-ignore lint/a11y/noAutofocus: <explanation>
                        autoFocus
                    />
                ) : (
                    <p className="text-white text-base truncate w-full overflow-hidden whitespace-nowrap pr-4">
                        {title}
                    </p>
                )}
                <p className="flex flex-row gap-1 text-white text-xs w-full text-ellipsis">
                    <span className="text-gray-60">Created</span>
                    {`${timeAgo(createdAt)}`}
                </p>
            </div>
        </div>
    );
};
